/**
 * The effort-hours model: one breakdown per version and delivery modality.
 *
 * Shared because the programme's per-credit defaults live on Delivery and the
 * per-module figures they seed live in Module Studio.
 *
 * @module components/shared/effort-hours
 */

import type { Programme } from "@/types";
import { isFeAwardType } from "@/utils/award-types";

/** One version's effort breakdown for a single delivery modality. */
export interface EffortData {
  classroomHours: number;
  classroomRatio: string;
  mentoringHours: number;
  mentoringRatio: string;
  otherContactHours: number;
  otherContactRatio: string;
  otherContactSpecify: string;
  directedElearningHours: number;
  independentLearningHours: number;
  otherHours: number;
  otherHoursSpecify: string;
  workBasedHours: number;
}

/** A programme version paired with one of its delivery modalities. */
export interface VersionModality {
  key: string;
  versionId: string;
  modality: string;
  label: string;
}

export const MODALITY_LABELS: Record<string, string> = {
  F2F: "Face-to-face",
  BLENDED: "Blended",
  ONLINE: "Fully online",
};

export const DEFAULT_EFFORT_DATA: EffortData = {
  classroomHours: 0,
  classroomRatio: "1:60",
  mentoringHours: 0,
  mentoringRatio: "1:25",
  otherContactHours: 0,
  otherContactRatio: "",
  otherContactSpecify: "",
  directedElearningHours: 0,
  independentLearningHours: 0,
  otherHours: 0,
  otherHoursSpecify: "",
  workBasedHours: 0,
};

/** The fields carrying hours, which scale with a module's credits. */
export const HOUR_FIELDS: readonly (keyof EffortData)[] = [
  "classroomHours",
  "mentoringHours",
  "otherContactHours",
  "directedElearningHours",
  "independentLearningHours",
  "otherHours",
  "workBasedHours",
];

/** Notional hours behind one credit: 10 for FET, 25 for ECTS. */
export function hoursPerCredit(programme: Pick<Programme, "awardType">): number {
  return isFeAwardType(programme.awardType ?? "") ? 10 : 25;
}

export function totalEffortHours(effort: Partial<EffortData>): number {
  const e = effort ?? {};
  return (
    Number(e.classroomHours ?? 0) +
    Number(e.mentoringHours ?? 0) +
    Number(e.otherContactHours ?? 0) +
    Number(e.directedElearningHours ?? 0) +
    Number(e.independentLearningHours ?? 0) +
    Number(e.otherHours ?? 0) +
    Number(e.workBasedHours ?? 0)
  );
}

/**
 * How a total reads against what the credit base asks for.
 *
 * An `expected` of 0 (no credits set yet, or a genuinely 0-credit module)
 * means there is nothing to be met, so an untouched 0/0 breakdown reads as
 * "nothing entered" rather than success.
 */
export function effortTone(total: number, expected: number): "success" | "warning" | "neutral" {
  if (expected > 0 && total === expected) {
    return "success";
  }
  return total > 0 ? "warning" : "neutral";
}

export function effortCategoryTotals(effort: Partial<EffortData>): {
  directed: number;
  independent: number;
  workBased: number;
} {
  return {
    directed:
      Number(effort.classroomHours ?? 0) +
      Number(effort.mentoringHours ?? 0) +
      Number(effort.otherContactHours ?? 0) +
      Number(effort.directedElearningHours ?? 0),
    independent: Number(effort.independentLearningHours ?? 0) + Number(effort.otherHours ?? 0),
    workBased: Number(effort.workBasedHours ?? 0),
  };
}

/** Every version that names a modality, as the pair effort is recorded against. */
export function versionModalities(programme: {
  versions?: readonly { id: string; label?: string; code?: string; deliveryModality?: string }[];
}): VersionModality[] {
  const versions = Array.isArray(programme.versions) ? programme.versions : [];
  return versions
    .filter((version) => version.deliveryModality)
    .map((version) => ({
      key: `${version.id}_${version.deliveryModality}`,
      versionId: version.id,
      modality: version.deliveryModality ?? "",
      label: `${version.label || version.code || "Version"} — ${
        MODALITY_LABELS[version.deliveryModality ?? ""] || version.deliveryModality
      }`,
    }));
}

/**
 * A per-credit pattern scaled to a module's own credits.
 *
 * Hours scale; ratios and the free-text names of the "other" categories are
 * carried across unchanged, because they describe the activity rather than
 * how much of it there is.
 */
export function scaleToCredits(
  pattern: Partial<EffortData>,
  moduleCredits: number,
  creditBase: number,
): Partial<EffortData> {
  const base = creditBase > 0 ? creditBase : 1;
  const scaled: Partial<EffortData> = { ...pattern };
  const exact = HOUR_FIELDS.map((field) => Number(pattern[field] ?? 0) * (moduleCredits / base));
  const floors = exact.map((value) => Math.floor(value));
  // Largest remainder rather than rounding each field alone: the summary
  // beside this compares the total against `credits x hoursPerCredit` by exact
  // equality, and seven independent roundings cannot be trusted to land there.
  // Applying the pattern then left every module it touched reading "Excess".
  const total = (values: readonly number[]): number => values.reduce((sum, n) => sum + n, 0);
  const short = Math.round(total(exact)) - total(floors);
  const order = exact
    .map((value, index) => ({ index, remainder: value - floors[index]! }))
    .sort((left, right) => right.remainder - left.remainder)
    .slice(0, Math.max(0, short))
    .map((entry) => entry.index);
  HOUR_FIELDS.forEach((field, index) => {
    (scaled as Record<string, unknown>)[field] = floors[index]! + (order.includes(index) ? 1 : 0);
  });
  return scaled;
}
